/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-unstable-nested-components */
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import moment from 'moment-timezone';
import { CSVLink } from 'react-csv';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Camera, ShieldAlert, AlertTriangle, ChevronRight, Download,
} from 'lucide-react';
import './reports.css';
import { Navbar, Sidebar } from '../../components';
import DateRangePicker from '../../components/dateRangePicker';
import FilterModal from './FilterModal';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { sendRequest } from '../../api/shootAPI';
import { getDatesInRange } from '../../utils/time';
import {
  generateWebcamReport, transformToGraphAuditData, transformToTableAuditData,
} from '../../utils/report';
import API from '../../api/endpoints';

const softShadow = 'shadow-[0_1px_2px_rgba(14,30,64,0.04)]';

const csvHeaders = [
  { label: 'Date', key: 'date' },
  { label: 'Rejected', key: 'Rejected' },
  { label: 'Taken', key: 'Taken' },
  { label: 'Score', key: 'Score' },
];

function KPI({
  icon: Icon, label, value, accent,
}) {
  return (
    <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} p-4`}>
      <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-3">{label}</p>
      <div className="flex items-center justify-between">
        <span
          className="text-3xl font-bold tabular-nums leading-none"
          style={{ color: accent }}
        >
          {value}
        </span>
        <span
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accent}1F`, color: accent }}
        >
          <Icon size={18} />
        </span>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-xl bg-white border border-surface-2 shadow-[0_8px_24px_rgba(14,30,64,0.08)] px-4 py-3 min-w-[180px]">
      <p className="text-sm font-semibold text-[#0e1e40] mb-2">{label}</p>
      <div className="space-y-1.5">
        <div className="flex items-center text-xs">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0 mr-2"
            style={{ backgroundColor: '#53A551' }}
          />
          <span className="text-ink-900 flex-1">Audits</span>
          <span className="text-ink-900 tabular-nums ml-6">
            {payload[0]?.value ?? 0}
          </span>
        </div>
      </div>
    </div>
  );
}

function DayRow({ day, isOpen, onToggle, idx }) {
  return (
    <div>
      <div className="px-5 py-2 bg-surface-page text-[11px] uppercase tracking-wider text-ink-500 font-semibold">
        {day.date}
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-[1fr_90px_90px_90px] sm:grid-cols-[1fr_120px_120px_120px] items-center px-5 py-3 text-sm hover:bg-surface-page transition"
      >
        <span className="flex items-center gap-2 text-ink-900 font-medium">
          <ChevronRight
            size={14}
            className={`text-ink-500 transition-transform ${isOpen ? 'rotate-90' : ''}`}
          />
          Total
        </span>
        <span className="text-right text-ink-900 tabular-nums">{day.Rejected}</span>
        <span className="text-right text-ink-900 tabular-nums">{day.Taken}</span>
        <span className="text-right pr-2 text-ink-900 tabular-nums">
          {(day.Taken + day.Rejected) > 0 ? Math.round(day.Score) : 0}
        </span>
      </button>
      {isOpen && day.data.length > 0 && day.data.map((it, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={`${idx}-${i}`}
          className="grid grid-cols-[1fr_90px_90px_90px] sm:grid-cols-[1fr_120px_120px_120px] items-center px-5 py-3 text-sm border-t border-surface-1"
        >
          <span className="pl-6 text-ink-900 tabular-nums">{it.Time}</span>
          <span className="text-right text-ink-900 tabular-nums">{it.Rejected}</span>
          <span className="text-right text-ink-900 tabular-nums">{it.Taken}</span>
          <span className="text-right pr-2 text-ink-500 tabular-nums">{it.Score}</span>
        </div>
      ))}
      {isOpen && day.data.length === 0 && (
        <div className="px-5 py-3 text-xs text-ink-500 italic border-t border-surface-1">
          No audits recorded.
        </div>
      )}
    </div>
  );
}

function WebcamAudits() {
  const today = new Date();
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const [date, setDate] = useState([new Date(moment(today).subtract(6, 'days')), today]);
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [webcamAudits, setWebcamAudits] = useState([]);
  const [webcamViolations, setWebcamViolations] = useState([]);
  const [teamId, setTeamId] = useState(0);
  const [memberId, setMemberId] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [displayText, setDisplayText] = useState('All');
  const [errorCount, setErrorCount] = useState(0);
  const [openDays, setOpenDays] = useState(new Set());

  const fetchTeams = async () => {
    const [startDate, endDate] = date;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.createTeam(activeWorkspace.id),
      timeout: 30000,
      params: { start: startOfDay, end: endOfDay, searchQuery: '' },
    });
    if (success) {
      setTeams(data.teams);
    } else {
      displayNotification({
        message: 'There was an issue fetching team data in this workspace. Please try again',
        severity: 'error',
      });
    }
  };

  const fetchMembers = async () => {
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.members(activeWorkspace.id),
      params: { page: 1, pageSize: 1000 },
    });
    if (success) {
      setMembers(data.members);
    } else {
      displayNotification({
        message: 'There was an issue fetching members data in this workspace. Please try again',
        severity: 'error',
      });
    }
  };

  const fetch = async (url, filter) => {
    const [startDate, endDate] = date;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const { success, data } = await sendRequest({
      method: 'get',
      url,
      params: { start: startOfDay, end: endOfDay },
    });
    if (success) {
      // eslint-disable-next-line chai-friendly/no-unused-expressions
      filter === 'Audit' ? setWebcamAudits(data) : setWebcamViolations(data);
    } else {
      displayNotification({
        message: 'There was an issue fetching data in this workspace. Please try again',
        severity: 'error',
      });
    }
  };

  const fetchAllData = () => {
    fetch(API.WORKSPACE.workspaceWebcamAudits(activeWorkspace.id), 'Audit');
    fetch(API.WORKSPACE.workspaceWebcamViolations(activeWorkspace.id), 'Violation');
  };

  const fetchData = (filterMemberId) => {
    fetch(API.MEMBER.webcamAudits(filterMemberId), 'Audit');
    fetch(API.MEMBER.webcamViolations(filterMemberId), 'Violation');
  };

  const handleSearch = async (filterTeamId, filterMemberId) => {
    setTeamId(filterTeamId);
    setMemberId(filterMemberId);
    if (filterTeamId === 0 && filterMemberId === 0) {
      setDisplayText('All');
      fetchAllData();
    } else if (filterTeamId !== 0) {
      const filteredTeam = teams.find((team) => team.id === filterTeamId);
      setDisplayText(filteredTeam?.name || 'All');
      const teamAudits = [];
      const teamViolations = [];
      filteredTeam?.teamMates?.forEach((mate) => {
        if (mate.member.webcamAudits?.length > 0) {
          mate.member.webcamAudits.forEach((item) => { teamAudits.push(item); });
        }
        if (mate.member.webcamViolations?.length > 0) {
          mate.member.webcamViolations.forEach((item) => { teamViolations.push(item); });
        }
      });
      setWebcamAudits(teamAudits);
      setWebcamViolations(teamViolations);
    } else {
      const filterMember = members.find((item) => item.id === filterMemberId);
      setDisplayText(filterMember ? `${filterMember.user.firstName} ${filterMember.user.lastName}` : 'All');
      fetchData(filterMemberId);
    }
  };

  useEffect(() => {
    if (teamId === 0 && memberId === 0) {
      setDisplayText('All');
      fetchAllData();
    } else if (teamId !== 0) {
      const filteredTeam = teams.find((team) => team.id === teamId);
      setDisplayText(filteredTeam?.name || 'All');
      const teamAudits = [];
      const teamViolations = [];
      filteredTeam?.teamMates?.forEach((m) => {
        if (m.member.webcamAudits?.length > 0) m.member.webcamAudits.forEach((item) => teamAudits.push(item));
        if (m.member.webcamViolations?.length > 0) m.member.webcamViolations.forEach((item) => teamViolations.push(item));
      });
      setWebcamAudits(teamAudits);
      setWebcamViolations(teamViolations);
    } else {
      const filterMember = members.find((item) => item.id === memberId);
      setDisplayText(filterMember ? `${filterMember.user.firstName} ${filterMember.user.lastName}` : 'All');
      fetchData(memberId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    fetchTeams();
    fetchMembers();
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const result = generateWebcamReport(webcamAudits, webcamViolations, date[0], date[1]);
    setChartData(transformToGraphAuditData(result));
    const table = transformToTableAuditData(webcamAudits, date[0], date[1]);
    setTableData(table);
    setExportData(table);
    const errors = webcamAudits.filter((audit) => audit.status !== 'Complete');
    setErrorCount(errors.length);

    const firstWithData = table.findIndex((d) => (d.Taken + d.Rejected) > 0);
    setOpenDays(firstWithData >= 0 ? new Set([firstWithData]) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamAudits, webcamViolations]);

  const auditChartData = useMemo(
    () => chartData.map((d) => ({ date: d.date, Audits: d.Audits })),
    [chartData],
  );

  const toggleDay = (idx) => {
    setOpenDays((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header">
          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="font-bold text-ink-900 leading-tight truncate text-2xl lg:text-[32px] lg:leading-10">
                Webcam Audit Results:&nbsp;
                {displayText}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:justify-end w-full sm:w-auto">
              <div className="flex-1 sm:flex-none min-w-0">
                <DateRangePicker
                  value={date}
                  onChange={(_date) => setDate(_date)}
                  className="w-auto"
                />
              </div>
              <FilterModal
                handleSearch={handleSearch}
                members={members}
                teams={teams}
                pending={{ teams: false, members: false }}
                isActive={teamId !== 0 || memberId !== 0}
              />
            </div>
          </div>

          <div className="space-y-5">
            {/* KPI strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <KPI
                icon={Camera}
                label="Total Number of Audits"
                value={webcamAudits.length}
                accent="#53A551"
              />
              <KPI
                icon={ShieldAlert}
                label="Total Number of Violations"
                value={webcamViolations.length}
                accent="#F19235"
              />
              <KPI
                icon={AlertTriangle}
                label="Total Number of Errors"
                value={errorCount}
                accent="#b94f3c"
              />
            </div>

            {/* Chart */}
            <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} p-5`}>
              <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-4">
                Daily Audit Volume
              </p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={auditChartData}
                    margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="auditFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#53A551" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#53A551" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e1e6" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#546077' }}
                      axisLine={{ stroke: '#e0e1e6' }}
                      tickLine={false}
                      interval="preserveStartEnd"
                      minTickGap={20}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#546077' }}
                      axisLine={{ stroke: '#e0e1e6' }}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      cursor={{ stroke: '#e0e1e6', strokeWidth: 1 }}
                      content={<ChartTooltip />}
                    />
                    <Area
                      type="monotone"
                      dataKey="Audits"
                      stroke="#53A551"
                      strokeWidth={2}
                      fill="url(#auditFill)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Daily Webcam Audit */}
            <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} overflow-hidden`}>
              <div className="flex items-center justify-between px-5 py-4">
                <h3 className="text-base font-semibold text-ink-900">Daily Webcam Audit</h3>
                <CSVLink data={exportData} headers={csvHeaders} filename="Report.csv">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rule rounded-[8px] text-xs font-semibold text-ink-900 bg-white hover:border-brand-500 hover:text-brand-700 transition"
                  >
                    <Download size={12} />
                    Export Table
                  </button>
                </CSVLink>
              </div>
              <div className="grid grid-cols-[1fr_90px_90px_90px] sm:grid-cols-[1fr_120px_120px_120px] items-center px-5 py-2.5 border-b border-surface-1 text-xs text-ink-500 font-medium">
                <span>Audit Time</span>
                <span className="text-right">Rejected</span>
                <span className="text-right">Taken</span>
                <span className="text-right pr-2">Score</span>
              </div>
              <div>
                {tableData.map((day, idx) => (
                  <DayRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    idx={idx}
                    day={day}
                    isOpen={openDays.has(idx)}
                    onToggle={() => toggleDay(idx)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default WebcamAudits;
